import { UploaderController, type ControllerOptions } from '../core/uploader-controller';
import { DEFAULT_ICONS, DEFAULT_LABELS, DEFAULT_THEME } from '../constants/defaults';
import type {
  IconSet,
  LabelSet,
  ThemeOptions,
  UploadItem,
  UploadSourceOption,
  UploaderClassNames,
} from '../types';
import { resolveIcon } from '../utils/helpers';
import { widgetStyles } from './styles';

const HTMLElementShim: typeof HTMLElement =
  typeof globalThis === 'undefined' || typeof globalThis.HTMLElement === 'undefined'
    ? (class HTMLElementFallback {} as unknown as typeof HTMLElement)
    : globalThis.HTMLElement;

interface FilesAddedDetail {
  items: UploadItem[];
}

interface FileSystemEntry {
  isFile: boolean;
  isDirectory: boolean;
  name: string;
  fullPath: string;
}

interface FileSystemFileEntry extends FileSystemEntry {
  file: (callback: (file: File) => void, errorCallback?: (error: DOMException) => void) => void;
}

interface FileSystemDirectoryEntry extends FileSystemEntry {
  createReader: () => FileSystemDirectoryReader;
}

interface FileSystemDirectoryReader {
  readEntries: (
    successCallback: (entries: FileSystemEntry[]) => void,
    errorCallback?: (error: DOMException) => void
  ) => void;
}

export class AutorenderUploaderElement extends HTMLElementShim {
  static tagName = 'autorender-uploader';

  private shadow: ShadowRoot;
  private controller: UploaderController | null = null;
  private options!: ControllerOptions;
  private labels: Required<LabelSet> = DEFAULT_LABELS;
  private icons: Required<IconSet> = DEFAULT_ICONS;
  private classNames: UploaderClassNames = {};
  private sourceOptions: UploadSourceOption[] = [];

  private triggerType: 'button' | 'inline' = 'inline';
  private isModalOpen = false;
  private fileLayout: 'list' | 'grid' = 'list';
  private showGridFileName = true;

  private rootElement: HTMLElement | null = null;
  private fileInput!: HTMLInputElement;
  private dropzone: HTMLDivElement | null = null;
  private fileList: HTMLUListElement | null = null;
  private sourceLists: HTMLElement[] = [];
  private modalBody: HTMLElement | null = null;
  private inlineUpload: HTMLElement | null = null;
  private uploadButton: HTMLButtonElement | null = null;
  private triggerButton: HTMLButtonElement | null = null;
  private modalElement: HTMLElement | null = null;
  private modalCloseButton: HTMLButtonElement | null = null;
  private modalDoneButton: HTMLButtonElement | null = null;
  private modalClearButton: HTMLButtonElement | null = null;
  private modalUploadButton: HTMLButtonElement | null = null;
  private modalCancelButton: HTMLButtonElement | null = null;
  private modalAddMoreButton: HTMLButtonElement | null = null;
  private modalFooterActions: HTMLElement | null = null;
  private modalSourceContainer: HTMLElement | null = null;
  private actionsSourceContainer: HTMLElement | null = null;
  private modalTitleElement: HTMLElement | null = null;
  private modalHeaderElement: HTMLElement | null = null;
  private modalPoweredElement: HTMLElement | null = null;
  private inlinePoweredElement: HTMLElement | null = null;
  private duplicateBanners: HTMLElement[] = [];
  private errorBannerElement: HTMLElement | null = null;
  private globalErrorMessage: string | null = null;
  private portalContainer: HTMLElement | null = null;
  private portalDiv: HTMLElement | null = null;
  private isInsideContainer: boolean = false;
  private progressLiveRegion: HTMLElement | null = null;
  private modalPreviouslyFocused: HTMLElement | null = null;

  private isAbortError(error: unknown): boolean {
    if (!error) return false;
    if (error instanceof DOMException && error.name === 'AbortError') return true;
    if (error instanceof Error && error.name === 'AbortError') return true;
    const message =
      error instanceof Error
        ? error.message
        : typeof error === 'string'
        ? error
        : '';
    return typeof message === 'string' && message.toLowerCase().includes('abort');
  }

  private controllerListeners = new Map<string, (event: Event) => void>();
  private handleKeydown = (event: KeyboardEvent) => {
    if (event.key === 'Escape' && this.isModalOpen) {
      this.isModalOpen = false;
      this.render();
      return;
    }

    if (event.key === 'Tab' && this.isModalOpen && this.modalElement) {
      const focusable = Array.from(
        this.modalElement.querySelectorAll<HTMLElement>(
          'button:not([disabled]):not([hidden]), [href], input:not([disabled]), [tabindex]:not([tabindex="-1"])'
        )
      ).filter(el => !el.hidden && el.style.display !== 'none');

      if (focusable.length === 0) return;

      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      const active = this.modalElement.getRootNode() instanceof ShadowRoot
        ? (this.modalElement.getRootNode() as ShadowRoot).activeElement
        : document.activeElement;

      if (event.shiftKey && active === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && active === last) {
        event.preventDefault();
        first.focus();
      }
    }
  };

  constructor() {
    super();
    this.shadow = this.attachShadow({ mode: 'open' });
  }

  connectedCallback() {
    this.render();
  }

  disconnectedCallback() {
    this.detachController();
    window.removeEventListener('keydown', this.handleKeydown);
    this.cleanupPortal();
  }

  private setupPortal() {
    if (!this.options.container) {
      this.isInsideContainer = false;
      return;
    }

    const container = typeof this.options.container === 'string'
      ? document.querySelector<HTMLElement>(this.options.container)
      : this.options.container;

    if (!container) {
      console.warn(`Container "${this.options.container}" not found`);
      this.isInsideContainer = false;
      return;
    }

    // Check if the widget is already inside the container
    // If so, we don't need a portal - everything is already contained
    let currentParent: Node | null = this.parentNode;
    while (currentParent && currentParent !== document.body) {
      if (currentParent === container) {
        // Widget is already inside container, no portal needed
        // But we need to ensure container has position relative for absolute modal positioning
        const containerStyle = window.getComputedStyle(container);
        if (containerStyle.position === 'static') {
          container.style.position = 'relative';
        }
        this.portalContainer = null;
        this.portalDiv = null;
        this.isInsideContainer = true;
        return;
      }
      currentParent = currentParent.parentNode;
    }

    // Widget is not inside container, use portal for modal only
    // Ensure container has position relative or absolute for absolute positioning
    const containerStyle = window.getComputedStyle(container);
    if (containerStyle.position === 'static') {
      container.style.position = 'relative';
    }

    this.portalContainer = container;
    this.isInsideContainer = false;

    // Create or reuse portal div
    if (!this.portalDiv) {
      this.portalDiv = document.createElement('div');
      this.portalDiv.className = 'ar-sdk-portal';
      this.portalDiv.style.position = 'absolute';
      this.portalDiv.style.inset = '0';
      this.portalDiv.style.zIndex = '1000';
      container.appendChild(this.portalDiv);
    }
  }

  private cleanupPortal() {
    if (this.portalDiv) {
      this.portalDiv.remove();
      this.portalDiv = null;
    }
    this.portalContainer = null;
    this.isInsideContainer = false;
  }

  setController(controller: UploaderController, options: ControllerOptions) {
    this.detachController();
    this.controller = controller;
    this.globalErrorMessage = null;
    this.applyOptions(options);
    this.attachController();
    this.render();
  }

  updateOptions(options: Partial<ControllerOptions>) {
    if (!this.controller) return;
    this.applyOptions({ ...this.options, ...options });
    this.controller.setOptions(this.options);
    this.render();
  }

  openFileDialog() {
    if (this.triggerType === 'button' && !this.isModalOpen) {
      this.isModalOpen = true;
      this.render();
      requestAnimationFrame(() => this.fileInput?.click());
      return;
    }
    this.fileInput?.click();
  }

  private applyOptions(options: ControllerOptions) {
    const previousOptions = this.options ?? ({} as ControllerOptions);
    const themeInput = (options.theme ?? previousOptions.theme ?? DEFAULT_THEME) as ThemeOptions;
    const resolvedTheme = {
      appearance: themeInput.appearance ?? DEFAULT_THEME.appearance,
      borderRadius: themeInput.borderRadius ?? DEFAULT_THEME.borderRadius,
      dropzoneBackground: themeInput.dropzoneBackground ?? DEFAULT_THEME.dropzoneBackground,
      dropzoneBorder: themeInput.dropzoneBorder ?? DEFAULT_THEME.dropzoneBorder,
      fontFamily: themeInput.fontFamily ?? previousOptions.theme?.fontFamily,
    };

    // Sources are already normalized by normalizeOptions (empty array if all disabled)
    const resolvedSources = options.sources ?? previousOptions.sources ?? [];
    const previousSourceIds = this.sourceOptions?.map(s => s.id).sort().join(',') ?? '';

    // Resolve container
    const containerChanged = previousOptions.container !== options.container;
    if (containerChanged && this.portalDiv && this.portalContainer) {
      // Clean up old portal
      this.portalDiv.remove();
      this.portalDiv = null;
      this.portalContainer = null;
    }

    this.options = {
      ...previousOptions,
      ...options,
      theme: resolvedTheme,
      sources: resolvedSources,
    };

    // Setup portal if container is specified and widget is not already inside it
    if (this.options.container) {
      this.setupPortal();
    } else {
      this.cleanupPortal();
    }

    this.labels = {
      ...DEFAULT_LABELS,
      ...(this.options.labels ?? {}),
    };

    this.icons = DEFAULT_ICONS;

    const currentSourceIds = resolvedSources.map(s => s.id).sort().join(',');
    const sourcesChanged = previousSourceIds !== currentSourceIds;
    
    this.sourceOptions = resolvedSources;
    this.options.onSourceSelect = options.onSourceSelect ?? previousOptions.onSourceSelect;

    const requestedType = this.options.type ?? 'inline';
    this.triggerType = requestedType === 'button' ? 'button' : 'inline';
    this.fileLayout = this.options.fileLayout ?? 'list';
    this.showGridFileName = this.options.showGridFileName ?? true;
    this.classNames = this.options.classNames ?? {};

    if (this.options.autoUpload === undefined && this.triggerType === 'inline') {
      this.options.autoUpload = true;
    }

    // Re-render source list if sources changed and widget is already rendered
    if (this.rootElement && sourcesChanged) {
      const hasItems = (this.controller?.getState().items.length ?? 0) > 0;
      this.renderSourceOptions(hasItems);
    }
  }

  private attachController() {
    if (!this.controller) return;

    const stateListener = () => this.updateState();
    const progressListener = () => this.updateState();
    const filesAddedListener = (event: Event) => {
      const detail = (event as CustomEvent<FilesAddedDetail>).detail;
      if (this.options.autoUpload && detail?.items?.length) {
        void this.controller?.uploadAll();
      }
    };
    const completeListener = () => {
      this.clearGlobalError();
      this.render();
    };
    const errorListener = (event: Event) => {
      const detail = (event as CustomEvent).detail;
      this.handleControllerError(detail);
    };

    this.controller.addEventListener('statechange', stateListener);
    this.controller.addEventListener('progress', progressListener);
    this.controller.addEventListener('fileprogress', progressListener);
    this.controller.addEventListener('filesadded', filesAddedListener);
    this.controller.addEventListener('complete', completeListener);
    this.controller.addEventListener('error', errorListener);

    this.controllerListeners.set('statechange', stateListener);
    this.controllerListeners.set('progress', progressListener);
    this.controllerListeners.set('fileprogress', progressListener);
    this.controllerListeners.set('filesadded', filesAddedListener);
    this.controllerListeners.set('complete', completeListener);
    this.controllerListeners.set('error', errorListener);
  }

  private detachController() {
    if (!this.controller) return;
    for (const [event, handler] of this.controllerListeners.entries()) {
      this.controller.removeEventListener(event, handler);
    }
    this.controllerListeners.clear();
    this.controller = null;
  }

  private resolveClassName(key: keyof UploaderClassNames, fallback: string) {
    const extra = this.classNames[key];
    return extra ? `${fallback} ${extra}` : fallback;
  }

  private render() {
    if (!this.controller) return;

    this.setAttribute('data-trigger', this.triggerType);

    const duplicateBannerMarkup = `<div class="ar-duplicate-banner" hidden></div>`;
    const errorBannerMarkup = `<div class="ar-error-banner" role="alert" hidden></div>`;

    const dropzoneMarkup = `
      <div class="${this.resolveClassName('dropzone', 'ar-dropzone')}" role="region" aria-label="File upload" tabindex="0">
        <div class="ar-dropzone-icon"></div>
        <p class="ar-description">${this.labels.dropzoneTitle}</p>
      </div>
    `;

    const fileListMarkup = `<ul class="${this.resolveClassName('fileList', 'ar-file-list')}"></ul>`;
    const sourceListMarkup = '<div class="ar-source-list"></div>';

    const modalHeaderMarkup = `
      <div class="ar-modal-header">
        <h3 class="ar-modal-title">${this.labels.title}</h3>
        <button type="button" class="ar-modal-close" aria-label="Close"><span class="ar-modal-close-icon"></span></button>
      </div>
    `;

    const inlineHeaderMarkup = `
      <div class="ar-modal-header is-inline">
        <h3 class="ar-modal-title">${this.labels.title}</h3>
      </div>
    `;

    const uploadPanelMarkup = `
      <div class="ar-modal-body">
        <div class="ar-modal-upload${this.triggerType === 'inline' ? ' is-inline' : ''}">
          ${dropzoneMarkup}
          ${errorBannerMarkup}
          <div class="ar-progress-live ar-sr-only" aria-live="polite" aria-atomic="true"></div>
          ${duplicateBannerMarkup}
          ${sourceListMarkup}
          ${fileListMarkup}
        </div>
      </div>
    `;

    const footerMarkup = `
      <div class="ar-modal-footer${this.triggerType === 'inline' ? ' is-inline' : ''}">
        <button type="button" class="ar-modal-clear">${this.labels.clear}</button>
        <button type="button" class="ar-modal-cancel">${this.labels.cancel}</button>
        <div class="ar-modal-footer-actions">
          <div class="ar-modal-sources"></div>
          <button type="button" class="ar-modal-add-more">${this.labels.addMore}</button>
          <button type="button" class="ar-modal-upload-button">${this.labels.uploadButton}</button>
          <button type="button" class="ar-modal-done">${this.labels.done}</button>
        </div>
      </div>
    `;

    // Add is-portal class if widget is inside container (for absolute positioning) or if using portal div
    const shouldUsePortalPositioning = this.portalDiv || this.isInsideContainer;
    const modalMarkup = this.triggerType === 'button'
      ? `
      <div class="${this.resolveClassName('modal', 'ar-modal')} ${this.isModalOpen ? 'is-open' : ''} ${shouldUsePortalPositioning ? 'is-portal' : ''}">
        <div class="ar-modal-backdrop"></div>
        <div class="ar-modal-dialog">
          ${modalHeaderMarkup}
          ${uploadPanelMarkup}
          ${footerMarkup}
          <div class="ar-modal-powered">Powered by Autorender</div>
        </div>
      </div>
    `
      : '';

    const inlineMarkup = this.triggerType === 'inline'
      ? `
        <div class="ar-inline-upload">
          <div class="ar-inline-panel">
            ${inlineHeaderMarkup}
            ${uploadPanelMarkup}
            ${footerMarkup}
          </div>
          <div class="ar-inline-powered">Powered by Autorender</div>
        </div>
      `
      : '';

    const template = document.createElement('template');
    template.innerHTML = `
      <style>${widgetStyles}</style>
      <div class="${this.resolveClassName('root', 'ar-uploader-root')}" data-trigger="${this.triggerType}">
        <input type="file" class="ar-file-input" hidden />
        ${
          this.triggerType === 'button'
            ? `<button type="button" class="${this.resolveClassName('triggerButton', 'ar-button-trigger')}"><span class="ar-button-icon"></span><span>${this.labels.selectButton}</span></button>`
            : inlineMarkup
        }
        ${this.portalDiv ? '' : modalMarkup}
      </div>
    `;

    this.shadow.innerHTML = '';
    this.shadow.appendChild(template.content.cloneNode(true));

    // Render modal in portal if container is specified
    if (this.portalDiv && this.triggerType === 'button') {
      const portalTemplate = document.createElement('template');
      portalTemplate.innerHTML = `<style>${widgetStyles}</style>${modalMarkup}`;
      this.portalDiv.innerHTML = '';
      this.portalDiv.appendChild(portalTemplate.content.cloneNode(true));
    } else if (this.portalDiv && !this.triggerType) {
      // Clean up portal if not needed
      this.portalDiv.innerHTML = '';
    }

    this.captureElements();
    this.applyDimensions();
    this.configureInputs();
    this.bindEvents();
    this.applyThemeVariables();
    this.updateState();

    if (this.triggerType === 'button') {
      if (this.isModalOpen) {
        window.addEventListener('keydown', this.handleKeydown);
        this.modalPreviouslyFocused = document.activeElement as HTMLElement | null;
        requestAnimationFrame(() => {
          (this.modalCloseButton ?? this.modalUploadButton ?? this.dropzone)?.focus();
        });
      } else {
        window.removeEventListener('keydown', this.handleKeydown);
        this.modalPreviouslyFocused?.focus?.();
        this.modalPreviouslyFocused = null;
      }
    }
  }

  private queryModalElement(selector: string): Element | null {
    // Query from portal first if it exists, otherwise from shadow
    if (this.portalDiv) {
      return this.portalDiv.querySelector(selector) || this.shadow.querySelector(selector);
    }
    return this.shadow.querySelector(selector);
  }

  private queryModalElements(selector: string): Element[] {
    // Query from portal first if it exists, otherwise from shadow
    if (this.portalDiv) {
      const portalResults = Array.from(this.portalDiv.querySelectorAll(selector));
      const shadowResults = Array.from(this.shadow.querySelectorAll(selector));
      // Combine results (portal takes precedence)
      const combined: Element[] = [];
      portalResults.forEach(el => combined.push(el));
      shadowResults.forEach(el => {
        if (!combined.includes(el)) combined.push(el);
      });
      return combined;
    }
    return Array.from(this.shadow.querySelectorAll(selector));
  }

  private captureElements() {
    this.rootElement = this.shadow.querySelector('.ar-uploader-root');
    this.fileInput = this.shadow.querySelector('.ar-file-input') as HTMLInputElement;
    this.dropzone = this.shadow.querySelector('.ar-dropzone');
    this.fileList = this.shadow.querySelector('.ar-file-list');
    this.sourceLists = Array.from(this.shadow.querySelectorAll('.ar-source-list')) as HTMLElement[];
    this.modalBody = this.queryModalElement('.ar-modal-body') as HTMLElement | null;
    this.inlineUpload = this.shadow.querySelector('.ar-inline-upload');
    this.uploadButton = this.shadow.querySelector('.ar-upload-button');
    this.triggerButton = this.shadow.querySelector('.ar-button-trigger');
    this.modalElement = this.queryModalElement('.ar-modal') as HTMLElement | null;
    this.modalCloseButton = this.queryModalElement('.ar-modal-close') as HTMLButtonElement | null;
    this.modalDoneButton = this.queryModalElement('.ar-modal-done') as HTMLButtonElement | null;
    this.modalClearButton = this.queryModalElement('.ar-modal-clear') as HTMLButtonElement | null;
    this.modalUploadButton = this.queryModalElement('.ar-modal-upload-button') as HTMLButtonElement | null;
    this.modalCancelButton = this.queryModalElement('.ar-modal-cancel') as HTMLButtonElement | null;
    this.modalAddMoreButton = this.queryModalElement('.ar-modal-add-more') as HTMLButtonElement | null;
    this.modalFooterActions = this.queryModalElement('.ar-modal-footer-actions') as HTMLElement | null;
    this.modalSourceContainer = this.queryModalElement('.ar-modal-sources') as HTMLElement | null;
    this.actionsSourceContainer = this.shadow.querySelector('.ar-actions-sources');
    this.modalTitleElement = this.queryModalElement('.ar-modal-title') as HTMLElement | null;
    this.modalHeaderElement = this.queryModalElement('.ar-modal-header') as HTMLElement | null;
    this.modalPoweredElement = this.queryModalElement('.ar-modal-powered') as HTMLElement | null;
    this.inlinePoweredElement = this.shadow.querySelector('.ar-inline-powered');
    this.duplicateBanners = this.queryModalElements('.ar-duplicate-banner') as HTMLElement[];
    this.errorBannerElement = this.queryModalElement('.ar-error-banner') as HTMLElement | null;
    this.progressLiveRegion = this.queryModalElement('.ar-progress-live') as HTMLElement | null;
    this.updateErrorBanner();

    const icon = resolveIcon(this.icons.upload);
    const iconContainer = this.shadow.querySelector('.ar-dropzone-icon');
    if (icon && iconContainer) {
      iconContainer.innerHTML = '';
      iconContainer.appendChild(icon);
    }
    const buttonIcon = resolveIcon(this.icons.upload);
    const triggerIconContainer = this.shadow.querySelector('.ar-button-icon');
    if (buttonIcon && triggerIconContainer) {
      triggerIconContainer.innerHTML = '';
      triggerIconContainer.appendChild(buttonIcon);
    }
    const closeIcon = resolveIcon(this.icons.close);
    const closeIconContainer = this.queryModalElement('.ar-modal-close-icon');
    if (closeIcon && closeIconContainer) {
      closeIconContainer.innerHTML = '';
      closeIconContainer.appendChild(closeIcon);
    }
  }

  private applyDimensions() {
    if (!this.rootElement) return;

    const requestedWidth = this.options.width;

    if (requestedWidth === undefined || requestedWidth === null || requestedWidth === '') {
      if (this.triggerType === 'inline') {
        this.rootElement.style.width = '100%';
      } else {
        this.rootElement.style.removeProperty('width');
      }
      return;
    }

    const normalizedWidth =
      typeof requestedWidth === 'number' ? `${requestedWidth}px` : requestedWidth;
    this.rootElement.style.width = normalizedWidth;
  }

  private configureInputs() {
    this.fileInput?.removeAttribute('webkitdirectory');
    this.fileInput?.removeAttribute('mozdirectory');
    this.fileInput?.removeAttribute('directory');

    if (this.options.allowMultiple === false) {
      this.fileInput?.removeAttribute('multiple');
    } else {
      this.fileInput?.setAttribute('multiple', '');
    }

    if (this.options.accept?.length) {
      this.fileInput.accept = this.options.accept.join(',');
    }
  }

  private bindEvents() {
    this.triggerButton?.addEventListener('click', () => {
      this.isModalOpen = true;
      this.render();
    });

    this.modalCloseButton?.addEventListener('click', () => {
      this.isModalOpen = false;
      this.render();
    });

    this.modalElement?.addEventListener('click', event => {
      const target = event.target as HTMLElement;
      if (!target) return;
      if (target === this.modalElement || target.classList.contains('ar-modal-backdrop')) {
        this.isModalOpen = false;
        this.render();
      }
    });

    this.modalUploadButton?.addEventListener('click', () => {
      if (!this.controller) return;
      if (this.controller.getState().isUploading) return;
      void this.controller.uploadAll();
    });

    this.modalDoneButton?.addEventListener('click', () => {
      if (!this.controller) return;
      const state = this.controller.getState();
      const allCompleted = state.items.length > 0 && state.items.every(item => item.status === 'completed');
      if (this.triggerType === 'inline') {
        if (state.isUploading) {
          return;
        }
        if (allCompleted || state.items.length === 0) {
          this.controller.reset();
          this.updateState();
        }
        return;
      }
      if (state.isUploading) {
        this.closeModal();
        return;
      }
      if (allCompleted) {
        this.closeModal();
        return;
      }
      this.closeModal();
    });

    this.modalClearButton?.addEventListener('click', () => {
      if (!this.controller) return;
      const { isUploading } = this.controller.getState();
      if (isUploading) {
        return;
      }
      this.controller.reset();
      this.updateState();
    });

    this.modalCancelButton?.addEventListener('click', () => {
      if (!this.controller) return;
      const currentState = this.controller.getState();
      if (currentState.isUploading) return;
      this.controller.reset();
      if (this.triggerType === 'inline') {
        this.updateState();
        return;
      }
      this.isModalOpen = false;
      this.render();
    });

    this.modalAddMoreButton?.addEventListener('click', () => {
      this.openFileDialog();
    });

    this.dropzone?.addEventListener('click', () => this.openFileDialog());
    this.dropzone?.addEventListener('keydown', event => {
      if (event.key === 'Enter' || event.key === ' ') {
        event.preventDefault();
        this.openFileDialog();
      }
    });
    this.dropzone?.addEventListener('dragenter', event => {
      event.preventDefault();
      this.dropzone?.classList.add('is-dragging');
    });
    this.dropzone?.addEventListener('dragover', event => {
      event.preventDefault();
      this.dropzone?.classList.add('is-dragging');
      if (event.dataTransfer) {
        event.dataTransfer.dropEffect = 'copy';
      }
    });
    this.dropzone?.addEventListener('dragleave', event => {
      if (event.target === this.dropzone) {
        this.dropzone?.classList.remove('is-dragging');
      }
    });
    this.dropzone?.addEventListener('drop', async event => {
      event.preventDefault();
      this.dropzone?.classList.remove('is-dragging');
      const files = await this.extractFilesFromEvent(event);
      if (files.length) {
        await this.controller?.addFiles(files);
      }
      if (this.fileInput) {
        this.fileInput.value = '';
      }
    });

    this.fileInput?.addEventListener('change', async () => {
      const files = this.fileInput?.files ? Array.from(this.fileInput.files) : [];
      const mapped = files.map(file => ({
        file,
        relativePath: (file as File & { webkitRelativePath?: string }).webkitRelativePath,
      }));
      await this.controller?.addFiles(mapped);
      if (this.fileInput) this.fileInput.value = '';
    });

    this.uploadButton?.addEventListener('click', () => {
      if (this.controller?.getState().isUploading) return;
      void this.controller?.uploadAll();
    });
  }

  private async extractFilesFromEvent(
    event: DragEvent
  ): Promise<Array<{ file: File; relativePath?: string }>> {
    const items = event.dataTransfer?.items;

    if (!items) {
      const files = event.dataTransfer?.files ? Array.from(event.dataTransfer.files) : [];
      return files.map(file => ({
        file,
        relativePath: (file as File & { webkitRelativePath?: string }).webkitRelativePath,
      }));
    }

    const entries = Array.from(items)
      .map(item => (item.webkitGetAsEntry ? item.webkitGetAsEntry() : null))
      .filter(Boolean) as FileSystemEntry[];

    const collected: Array<{ file: File; relativePath?: string }> = [];

    for (const entry of entries) {
      const files = await this.readEntry(entry, '');
      collected.push(...files);
    }

    return collected;
  }

  private readEntry(entry: FileSystemEntry, path = ''): Promise<Array<{ file: File; relativePath?: string }>> {
    return new Promise(resolve => {
      if (entry.isFile) {
        (entry as FileSystemFileEntry).file(file => {
          resolve([{ file, relativePath: path ? `${path}/${file.name}` : file.name }]);
        });
      } else if (entry.isDirectory) {
        const reader = (entry as FileSystemDirectoryEntry).createReader();
        reader.readEntries(async entries => {
          const results = await Promise.all(
            entries.map(child => this.readEntry(child, path ? `${path}/${entry.name}` : entry.name))
          );
          resolve(results.flat());
        });
      } else {
        resolve([]);
      }
    });
  }

  private closeModal() {
    this.isModalOpen = false;
    this.render();
  }

  private setGlobalError(message: string | null) {
    this.globalErrorMessage = message;
    this.updateErrorBanner();
  }

  private clearGlobalError() {
    this.setGlobalError(null);
  }

  private updateErrorBanner() {
    if (!this.errorBannerElement) return;
    if (this.globalErrorMessage) {
      this.errorBannerElement.hidden = false;
      this.errorBannerElement.textContent = this.globalErrorMessage;
    } else {
      this.errorBannerElement.hidden = true;
      this.errorBannerElement.textContent = '';
    }
  }

  private handleControllerError(error: unknown) {
    if (this.isAbortError(error)) {
      this.clearGlobalError();
      return;
    }

    const status =
      typeof error === 'object' && error !== null && 'status' in error
        ? (error as any).status
        : undefined;
    const messageCandidate =
      error instanceof Error
        ? error.message
        : typeof (error as any)?.message === 'string'
        ? (error as any).message
        : typeof error === 'string'
        ? error
        : '';

    const normalized = String(messageCandidate ?? '').toLowerCase();
    const isAuthError =
      (typeof status === 'number' && (status === 401 || status === 403)) ||
      normalized.includes('unauthorized') ||
      normalized.includes('forbidden') ||
      normalized.includes('invalid api key');

    if (isAuthError) {
      this.setGlobalError(this.labels.invalidApiKey);
    } else if (messageCandidate) {
      this.setGlobalError(String(messageCandidate));
    } else {
      this.setGlobalError(this.labels.uploadFailed);
    }
  }

  private updateState() {
    if (!this.controller) return;
    if (!this.fileList) return;

    const state = this.controller.getState();
    const { items, isUploading, duplicates } = state;
    const hasItems = items.length > 0;
    const hasAuthError = items.some(item => item.error === 'invalid-api-key');

    if (hasAuthError) {
      this.setGlobalError(this.labels.invalidApiKey);
    }

    if (this.rootElement) {
      this.rootElement.classList.toggle('is-uploading', isUploading);
      this.rootElement.classList.toggle('has-items', hasItems);
    }

    if (this.dropzone) {
      if (hasItems) {
        this.dropzone.style.display = 'none';
      } else {
        this.dropzone.style.display = 'flex';
      }
    }

    this.renderFileList(items);
    this.updateErrorBanner();
    this.renderSourceOptions(hasItems);
    this.updateModalTitle(items, isUploading);
    this.updateDuplicateBanners(duplicates);

    if (this.progressLiveRegion) {
      if (isUploading) {
        const uploading = items.filter(item => item.status === 'uploading');
        const count = uploading.length;
        const average =
          count > 0
            ? Math.round(
                uploading.reduce((sum, item) => sum + (item.progress ?? 0), 0) / count
              )
            : 0;
        this.progressLiveRegion.textContent = `Uploading ${count} file${count === 1 ? '' : 's'}, ${average}% complete`;
      } else if (hasItems && items.every(item => item.status === 'completed')) {
        this.progressLiveRegion.textContent = 'Upload complete';
      } else {
        this.progressLiveRegion.textContent = '';
      }
    }

    if (this.modalHeaderElement) {
      this.modalHeaderElement.style.display = hasItems ? '' : 'none';
    }
    if (this.modalPoweredElement) {
      this.modalPoweredElement.style.display = hasItems ? 'none' : '';
    }
    if (this.inlinePoweredElement) {
      this.inlinePoweredElement.style.display = hasItems ? 'none' : '';
    }

    if (this.triggerType === 'button' || this.triggerType === 'inline') {
      const completedCount = items.filter(item => item.status === 'completed').length;
      const allCompleted = hasItems && completedCount === items.length && completedCount > 0;

      if (this.modalFooterActions) {
        this.modalFooterActions.style.display = hasItems ? '' : 'none';
      }

      const uploadButton = this.modalUploadButton;
      const doneButton = this.modalDoneButton;
      const cancelButton = this.modalCancelButton;
      const clearButton = this.modalClearButton;
      const addMoreButton = this.modalAddMoreButton;

      const showUpload = hasItems && !isUploading && !allCompleted;
      const showAddMore = hasItems && !isUploading && !allCompleted;
      const showDone = hasItems && (isUploading || allCompleted);
      const showClear = hasItems && (isUploading || allCompleted);
      const showInlineCancel =
        this.triggerType === 'inline' && hasItems && !isUploading && !allCompleted;
      const showButtonCancel =
        this.triggerType === 'button' && !isUploading && (!allCompleted || !hasItems);
      const showCancel = showInlineCancel || showButtonCancel;

      if (uploadButton) {
        uploadButton.style.display = showUpload ? '' : 'none';
        uploadButton.toggleAttribute('disabled', !showUpload);
      }

      if (doneButton) {
        doneButton.style.display = showDone ? '' : 'none';
        doneButton.textContent = this.labels.done;
        doneButton.toggleAttribute('disabled', !showDone);
      }

      if (clearButton) {
        clearButton.style.display = showClear ? '' : 'none';
        clearButton.toggleAttribute('disabled', isUploading);
      }

      if (cancelButton) {
        cancelButton.style.display = showCancel ? '' : 'none';
        cancelButton.classList.toggle(
          'is-fullwidth',
          this.triggerType === 'button' && !hasItems
        );
      }

      if (addMoreButton) {
        addMoreButton.style.display = showAddMore ? '' : 'none';
        addMoreButton.toggleAttribute('disabled', !showAddMore);
      }
    }
  }

  private renderFileList(items: UploadItem[]) {
    if (!this.fileList) return;
    this.fileList.innerHTML = '';
    if (items.length === 0) {
      this.fileList.style.display = 'none';
      this.fileList.classList.remove('is-grid');
      return;
    }

    const isGridLayout = this.fileLayout === 'grid';
    this.fileList.style.display = isGridLayout ? 'grid' : 'flex';
    this.fileList.classList.toggle('is-grid', isGridLayout);
    if (isGridLayout) {
      this.fileList.style.gridTemplateColumns = 'repeat(auto-fill, minmax(140px, 1fr))';
      this.fileList.style.gap = '1rem';
    } else {
      this.fileList.style.removeProperty('grid-template-columns');
      this.fileList.style.removeProperty('gap');
    }

    const isUploading = this.controller?.getState().isUploading ?? false;

    items.forEach(item => {
      const li = document.createElement('li');
      li.className = 'ar-file-item';
      li.classList.toggle('is-grid', isGridLayout);

      const isInvalidApiKeyError = item.error === 'invalid-api-key';

      if (item.status === 'uploading' || item.status === 'pending') li.classList.add('is-uploading');
      if (item.status === 'completed') li.classList.add('is-completed');
      if (item.status === 'error' && !isInvalidApiKeyError) li.classList.add('is-error');

      const header = document.createElement('div');
      header.className = 'ar-file-header';
      header.classList.toggle('is-grid', isGridLayout);

      const thumb = document.createElement('div');
      thumb.className = 'ar-file-thumb';
      thumb.classList.toggle('is-grid', isGridLayout);
      const isImage = item.file.type.startsWith('image/');
      if (item.previewUrl && isImage) {
        const img = document.createElement('img');
        img.src = item.previewUrl;
        img.alt = item.file.name;
        thumb.appendChild(img);
      } else {
        // Show document icon for non-images
        thumb.classList.add('ar-file-thumb-document');
        const documentIcon = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
        documentIcon.setAttribute('viewBox', '0 0 24 24');
        documentIcon.setAttribute('width', '24');
        documentIcon.setAttribute('height', '24');
        documentIcon.setAttribute('fill', 'none');
        documentIcon.setAttribute('aria-hidden', 'true');
        const path1 = document.createElementNS('http://www.w3.org/2000/svg', 'path');
        path1.setAttribute('d', 'M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8l-6-6Z');
        path1.setAttribute('stroke', 'currentColor');
        path1.setAttribute('stroke-width', '1.5');
        path1.setAttribute('stroke-linecap', 'round');
        path1.setAttribute('stroke-linejoin', 'round');
        const path2 = document.createElementNS('http://www.w3.org/2000/svg', 'path');
        path2.setAttribute('d', 'M14 2v6h6');
        path2.setAttribute('stroke', 'currentColor');
        path2.setAttribute('stroke-width', '1.5');
        path2.setAttribute('stroke-linecap', 'round');
        path2.setAttribute('stroke-linejoin', 'round');
        documentIcon.appendChild(path1);
        documentIcon.appendChild(path2);
        thumb.appendChild(documentIcon);
      }

      if (item.status === 'completed') {
        const statusOverlay = document.createElement('div');
        statusOverlay.className = 'ar-thumb-status';
        const successIcon = resolveIcon(this.icons.success);
        if (successIcon) {
          statusOverlay.appendChild(successIcon);
        }
        thumb.appendChild(statusOverlay);
      } else if (item.status === 'error' && !isInvalidApiKeyError) {
        const statusOverlay = document.createElement('div');
        statusOverlay.className = 'ar-thumb-status is-error';
        const errorIcon = resolveIcon(this.icons.error);
        if (errorIcon) {
          statusOverlay.appendChild(errorIcon);
        }
        thumb.appendChild(statusOverlay);
      }

      const name = document.createElement('span');
      name.className = 'ar-file-name';
      name.textContent = item.file.name;
      if (isGridLayout && !this.showGridFileName) {
        name.style.display = 'none';
      } else {
        name.style.display = '';
      }

      const removeButton = document.createElement('button');
      removeButton.type = 'button';
      removeButton.className = 'ar-remove-button';
      const shouldShowRemove =
        (item.status === 'pending' ||
          (item.status === 'error' && isInvalidApiKeyError)) &&
        !isUploading;
      removeButton.disabled = !shouldShowRemove;
      removeButton.hidden = !shouldShowRemove;
      removeButton.style.display = shouldShowRemove ? 'inline-flex' : 'none';
      removeButton.classList.toggle('is-grid', isGridLayout);
      removeButton.setAttribute('aria-label', this.labels.remove);
      const removeIcon = resolveIcon(this.icons.remove);
      if (removeIcon) {
        removeButton.appendChild(removeIcon);
      }
      removeButton.addEventListener('click', () => {
        if (this.controller?.getState().isUploading) return;
        this.controller?.removeFile(item.id);
      });

      header.appendChild(thumb);
      header.appendChild(name);
      header.appendChild(removeButton);
      li.appendChild(header);

      // Display info message (not error) if present - show it right after header
      if (item.info) {
        const metaRow = document.createElement('div');
        metaRow.className = 'ar-file-meta';
        const infoText = document.createElement('span');
        infoText.className = 'ar-file-info';
        infoText.textContent = item.info;
        metaRow.appendChild(infoText);
        li.appendChild(metaRow);
      }

      const progressBorder = document.createElement('div');
      progressBorder.className = 'ar-progress-border';
      progressBorder.setAttribute('role', 'progressbar');
      progressBorder.setAttribute('aria-valuemin', '0');
      progressBorder.setAttribute('aria-valuemax', '100');
      progressBorder.setAttribute('aria-label', `Upload progress for ${item.file.name}`);

      const progressFill = document.createElement('div');
      progressFill.className = 'ar-progress-fill';

      const isPendingStatus = item.status === 'pending';
      const isUploadingStatus = item.status === 'uploading';
      const isCompleteStatus = item.status === 'completed';
      const rawProgress = Math.max(0, Math.min(100, item.progress ?? 0));
      const calculatedTrain = !isCompleteStatus && rawProgress < 20;
      const shouldAnimateTrain = (isUploadingStatus || (isUploading && isPendingStatus)) && calculatedTrain;
      // Keep indicator active if uploading, even if progress hasn't updated recently
      const isActive = isUploadingStatus || shouldAnimateTrain || (rawProgress > 0 && !isCompleteStatus);
      const stateLabel = isCompleteStatus ? 'complete' : shouldAnimateTrain ? 'train' : rawProgress >= 20 ? 'progress' : isUploadingStatus ? 'progress' : 'idle';

      progressBorder.dataset.state = stateLabel;
      progressBorder.dataset.status = item.status;
      progressBorder.dataset.progress = rawProgress.toFixed(0);
      progressBorder.setAttribute('aria-valuenow', rawProgress.toFixed(0));

      progressBorder.classList.toggle('is-active', isActive);
      progressBorder.classList.toggle('is-train', shouldAnimateTrain);
      progressBorder.classList.toggle('is-complete', isCompleteStatus);
      progressBorder.style.display = isCompleteStatus ? 'none' : '';

      progressFill.classList.toggle('is-train', shouldAnimateTrain);
      progressFill.classList.toggle('is-animating', shouldAnimateTrain);

      if (isCompleteStatus) {
        progressFill.style.width = '100%';
      } else if (shouldAnimateTrain) {
        progressFill.style.width = '100%';
      } else if (isUploadingStatus && rawProgress === 0) {
        // Show minimal progress for uploading items even if progress hasn't started
        progressFill.style.width = '3%';
      } else {
        const computedWidth = Math.max(rawProgress, 3);
        progressFill.style.width = `${Math.min(computedWidth, 100)}%`;
      }

      progressBorder.appendChild(progressFill);

      li.appendChild(progressBorder);

      this.fileList?.appendChild(li);
    });
  }

  private renderSourceOptions(hasItems: boolean) {
    // Filter out disabled sources - only show enabled ones
    const enabledSources = this.sourceOptions.filter(option => !option.disabled);
    const hasSourcesConfigured = enabledSources.length > 0;

    const renderChoiceList = (container: HTMLElement) => {
      container.innerHTML = '';
      if (!hasSourcesConfigured) {
        container.style.display = 'none';
        container.classList.remove('ar-source-list--choices');
        return;
      }

      container.style.display = hasItems ? 'none' : 'flex';
      container.classList.toggle('ar-source-list--choices', !hasItems);
      if (hasItems) return;

      // Only render enabled sources
      enabledSources.forEach(option => {
        const button = document.createElement('button');
        button.type = 'button';
        button.className = 'ar-source-option';

        const icon = resolveIcon(option.icon ?? this.icons.upload);
        if (icon) {
          const iconWrapper = document.createElement('span');
          iconWrapper.className = 'ar-source-option-icon';
          iconWrapper.appendChild(icon);
          button.appendChild(iconWrapper);
        }

        const label = document.createElement('span');
        label.className = 'ar-source-option-label';
        label.textContent = option.label;
        button.appendChild(label);

        button.addEventListener('click', () => this.handleSourceOption(option));
        container.appendChild(button);
      });
    };

    this.sourceLists.forEach(container => {
      renderChoiceList(container);
    });

    if (this.modalSourceContainer) {
      this.modalSourceContainer.classList.toggle('is-visible', hasSourcesConfigured && !hasItems);
      this.modalSourceContainer.style.display = hasSourcesConfigured ? '' : 'none';
      this.modalSourceContainer.innerHTML = '';
    }
    if (this.actionsSourceContainer) {
      this.actionsSourceContainer.classList.toggle('is-visible', hasSourcesConfigured && !hasItems);
      this.actionsSourceContainer.style.display = hasSourcesConfigured ? '' : 'none';
      this.actionsSourceContainer.innerHTML = '';
    }

    // Add class to modal body and inline upload when sources are empty in inline mode
    if (this.triggerType === 'inline') {
      if (this.modalBody) {
        this.modalBody.classList.toggle('ar-modal-body--no-sources', !hasSourcesConfigured);
      }
      if (this.inlineUpload) {
        this.inlineUpload.classList.toggle('ar-inline-upload--no-sources', !hasSourcesConfigured);
      }
    }
  }

  private updateModalTitle(items: UploadItem[], isUploading: boolean) {
    if (!this.modalTitleElement) return;
    if (items.length === 0) {
      this.modalTitleElement.textContent = isUploading
        ? this.labels.uploading
        : this.labels.title;
      this.modalTitleElement.classList.remove('is-dynamic', 'is-success');
      return;
    }

    const total = items.length;
    const modalTitle = this.modalTitleElement;

    const suffix = total === 1 ? 'file' : 'files';
    const completedCount = items.filter(item => item.status === 'completed').length;

    modalTitle.classList.add('is-dynamic');
    modalTitle.classList.toggle('is-success', completedCount === total);

    if (isUploading) {
      modalTitle.textContent = `Uploading ${completedCount}/${total} ${suffix}`;
      return;
    }

    if (completedCount === total) {
      modalTitle.textContent = `${total} ${suffix} uploaded`;
      return;
    }

    modalTitle.textContent = `${total} ${suffix} ready`;
  }

  private updateDuplicateBanners(duplicates: string[]) {
    const hasDuplicates = duplicates.length > 0;
    const prefix =
      duplicates.length === 1
        ? this.labels.duplicateFileExists
        : this.labels.duplicateFilesExist;
    const message = hasDuplicates
      ? `${prefix} ${duplicates.join(', ')}`
      : '';

    this.duplicateBanners.forEach(banner => {
      banner.hidden = !hasDuplicates;
      banner.textContent = message;
    });
  }

  private handleSourceOption(option: UploadSourceOption) {
    if (option.disabled) return;

    this.options.onSourceSelect?.(option);
    this.dispatchEvent(new CustomEvent('sourceselect', { detail: option }));

    if (option.kind === 'device' && !option.action) {
      this.openFileDialog();
      return;
    }

    if (option.action) {
      try {
        option.action();
      } catch (error) {
        console.error('[AutorenderUploader] Source action failed', error);
      }
      return;
    }

    console.warn('[AutorenderUploader] No handler configured for source option', option);
  }

  private applyThemeVariables() {
    const theme = (this.options.theme ?? {}) as ThemeOptions;
    const borderRadius = theme.borderRadius;
    const accentColor = theme.accentColor;
    const dropzoneBackground = theme.dropzoneBackground ?? DEFAULT_THEME.dropzoneBackground;
    const dropzoneBorder = theme.dropzoneBorder ?? DEFAULT_THEME.dropzoneBorder;
    const fontFamily = theme.fontFamily;

    if (borderRadius !== undefined) {
      this.style.setProperty(
        '--ar-radius',
        typeof borderRadius === 'number' ? `${borderRadius}px` : String(borderRadius)
      );
    } else {
      this.style.removeProperty('--ar-radius');
    }

    // Only set accent color if explicitly provided; otherwise let palette CSS handle it
    if (accentColor) {
      this.style.setProperty('--ar-color-accent', accentColor);
    } else {
      this.style.removeProperty('--ar-color-accent');
    }

    if (dropzoneBackground) {
      this.style.setProperty('--ar-dropzone-bg', dropzoneBackground);
    }
    if (dropzoneBorder) {
      this.style.setProperty('--ar-dropzone-border', dropzoneBorder);
    }
    if (fontFamily) {
      this.style.setProperty('--ar-font-family', fontFamily);
    }

    const appearance = theme.appearance ?? DEFAULT_THEME.appearance;
    let resolvedAppearance = appearance;
    if (appearance === 'system' && typeof window !== 'undefined') {
      resolvedAppearance = window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
    }
    this.setAttribute('data-theme', resolvedAppearance);

    // Apply palette
    const palette = this.options.palette;
    if (palette) {
      this.setAttribute('data-palette', palette);
    } else {
      this.removeAttribute('data-palette');
    }
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'autorender-uploader': AutorenderUploaderElement;
  }
}